/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * CAWG identity-assertion validation (CAWG Identity 1.2 §7–§8): before the
 * reported identity summary is trusted, the identity assertion's COSE signature
 * (for `cawg.x509.cose`) and its `signer_payload.referenced_assertions` binding
 * to the enclosing claim are verified. Mirrors the c2pa-rs fork
 * (`feat/c2pa-cawg-untrusted-differentiation`,
 * `identity/identity_assertion/{assertion,signer_payload}.rs`,
 * `identity/x509/x509_signature_verifier.rs`).
 *
 * All outcomes are **non-gating** for `validation_state` (only the claim signer
 * drives Trusted); they harden the emitted CAWG code + reported identity so a
 * tampered/mis-bound identity is not surfaced as genuine. Success codes
 * (`cawg.identity.well-formed`/`.trusted`) belong in `validation_results` (v2)
 * and are deferred to the v2 validation_results work; here we surface the failure codes and withhold
 * the summary of a signature-invalid X.509 identity.
 */

import { decodeSign1, x5chain } from '../cose/decodeSign1'
import { verifyCoseSign1 } from '../cose/verify'
import { parseCertificate } from '../x509/parseCert'
import { decodeFirst } from '../cbor/decode'
import { assertionByLabel, type ParsedManifest } from '../model/parseManifestStore'
import type { HashedUriRef } from '../claim/parseClaim'

const CAWG_X509 = 'cawg.x509.cose'
const CAWG_ICA = 'cawg.identity_claims_aggregation'

export interface CawgIdentityValidation {
    /** A `cawg.identity` assertion is present on this manifest. */
    present: boolean
    /** The `signer_payload.sig_type`, or null when absent/malformed. */
    sigType: string | null
    /**
     * Whether the embedded identity COSE signature verified over the
     * `signer_payload` (X.509 form). `null` when not applicable (ICA form —
     * cryptographic verification deferred — or no cert). `false` withholds the
     * reported `signature_info` summary (§8, do not surface as validated).
     */
    signatureValid: boolean | null
    /** Non-gating failure codes to surface in `validation_status`. */
    codes: string[]
}

/**
 * Byte-exact raw CBOR span of a named member of a top-level CBOR map. The COSE
 * signature is computed over the original `signer_payload` CBOR bytes; a
 * re-serialization could differ in key ordering/encoding, so the exact bytes
 * must be sliced from the assertion (§8.2 — the detached payload is the
 * `signer_payload` CBOR).
 */
function cborMemberRaw (bytes: Uint8Array, name: string): Uint8Array | null {
    if (bytes.length === 0 || (bytes[0] >> 5) !== 5) return null // major type 5 (map)
    const ai = bytes[0] & 0x1f
    let pos = 1
    let n: number
    if (ai < 24) n = ai
    else if (ai === 24) { n = bytes[pos]; pos += 1 }
    else if (ai === 25) { n = (bytes[pos] << 8) | bytes[pos + 1]; pos += 2 }
    else if (ai === 26) { n = ((bytes[pos] << 24) | (bytes[pos + 1] << 16) | (bytes[pos + 2] << 8) | bytes[pos + 3]) >>> 0; pos += 4 }
    else return null // indefinite-length / 64-bit maps not expected in an assertion → withhold
    for (let i = 0; i < n; i++) {
        const k = decodeFirst(bytes, pos); pos = k.offset
        const valStart = pos
        const v = decodeFirst(bytes, pos); pos = v.offset
        if (k.value === name) return bytes.subarray(valStart, pos)
    }
    return null
}

/** A pad field is valid iff absent or an all-0x00 byte string (CAWG §7.1). */
function padIsZero (pad: unknown): boolean {
    if (pad === undefined || pad === null) return true
    if (!(pad instanceof Uint8Array)) return false
    return pad.every(b => b === 0)
}

/** The assertion label a CAWG hashed-URI resolves to (last path segment). */
function labelOfUri (url: string): string {
    const slash = url.lastIndexOf('/')
    return slash >= 0 ? url.slice(slash + 1) : url
}

function bytesEqual (a: Uint8Array | undefined, b: Uint8Array | undefined): boolean {
    if (!a || !b || a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
    return true
}

/**
 * Checks `signer_payload.referenced_assertions` against the enclosing claim
 * (CAWG §7.1): each referenced assertion must exist in the claim with a matching
 * hash (else `cawg.identity.assertion.mismatch`), none may be duplicated
 * (`cawg.identity.assertion.duplicate`), and at least one must be a hard-binding
 * assertion (`cawg.identity.hard_binding_missing`). Mirrors
 * `SignerPayload::check_against_manifest`.
 */
export function checkReferencedAssertions (refs: HashedUriRef[], claimRefs: HashedUriRef[]): string[] {
    const codes: string[] = []
    const claimByLabel = new Map<string, HashedUriRef>()
    for (const c of claimRefs) claimByLabel.set(c.label, c)

    const seen = new Set<string>()
    let hasHardBinding = false
    for (const ref of refs) {
        const label = labelOfUri(ref.url)
        if (label.startsWith('c2pa.hash.')) hasHardBinding = true
        if (seen.has(label)) {
            if (!codes.includes('cawg.identity.assertion.duplicate')) codes.push('cawg.identity.assertion.duplicate')
        }
        seen.add(label)
        const claimRef = claimByLabel.get(label)
        if (!claimRef) {
            if (!codes.includes('cawg.identity.assertion.mismatch')) codes.push('cawg.identity.assertion.mismatch')
        } else if (ref.hash && claimRef.hash && !bytesEqual(ref.hash, claimRef.hash)) {
            if (!codes.includes('cawg.identity.assertion.mismatch')) codes.push('cawg.identity.assertion.mismatch')
        }
    }
    if (!hasHardBinding) codes.push('cawg.identity.hard_binding_missing')
    return codes
}

/** Extracts the `referenced_assertions` hashed-URIs from a decoded signer_payload. */
function referencedAssertions (signerPayload: Record<string, unknown>): HashedUriRef[] {
    const ra = signerPayload.referenced_assertions
    if (!Array.isArray(ra)) return []
    const out: HashedUriRef[] = []
    for (const e of ra) {
        if (!e || typeof e !== 'object') continue
        const o = e as Record<string, unknown>
        const url = typeof o.url === 'string' ? o.url : ''
        if (!url) continue
        out.push({ url, label: labelOfUri(url), hash: o.hash instanceof Uint8Array ? o.hash : undefined })
    }
    return out
}

/**
 * Validates the active manifest's `cawg.identity` assertion. Non-gating: the
 * returned codes are surfaced in `validation_status` but do not change
 * `validation_state`; `signatureValid === false` tells the mapper to withhold
 * the X.509 identity's `signature_info` summary.
 */
export async function validateCawgIdentity (m: ParsedManifest): Promise<CawgIdentityValidation> {
    const a = assertionByLabel(m, 'cawg.identity')
    if (!a) return { present: false, sigType: null, signatureValid: null, codes: [] }

    // §15.10.3.1 / CAWG §7.1: a malformed identity assertion → cawg.identity.cbor.invalid.
    if (a.decodeError === 'cbor' || !a.decoded || typeof a.decoded !== 'object') {
        return { present: true, sigType: null, signatureValid: false, codes: ['cawg.identity.cbor.invalid'] }
    }

    const d = a.decoded as { signer_payload?: unknown, signature?: unknown, pad1?: unknown, pad2?: unknown }
    const sp = d.signer_payload
    if (!sp || typeof sp !== 'object') {
        return { present: true, sigType: null, signatureValid: false, codes: ['cawg.identity.cbor.invalid'] }
    }
    const signerPayload = sp as Record<string, unknown>
    const sigType = typeof signerPayload.sig_type === 'string' ? signerPayload.sig_type : null

    const codes: string[] = []
    // B — referenced_assertions binding (applies to every recognized sig_type).
    const claimRefs = [...m.claim.createdAssertions, ...m.claim.gatheredAssertions]
    codes.push(...checkReferencedAssertions(referencedAssertions(signerPayload), claimRefs))

    // Pad check (CAWG §7.1 l.996-997): pad1/pad2 MUST contain only 0x00 bytes;
    // otherwise report cawg.identity.pad.invalid (one code even if both are invalid).
    // The pads sit inside the claim-hashed assertion but outside the signed
    // signer_payload, so nonzero bytes there would otherwise be silently accepted.
    if (!padIsZero(d.pad1) || !padIsZero(d.pad2)) {
        codes.push('cawg.identity.pad.invalid')
    }

    // C — unknown sig_type diagnostic.
    if (sigType !== CAWG_X509 && sigType !== CAWG_ICA) {
        codes.push('cawg.identity.sig_type.unknown')
        return { present: true, sigType, signatureValid: false, codes }
    }

    // A — verify the X.509 identity COSE signature over the signer_payload CBOR.
    // For an x509.cose identity the signature is REQUIRED and must verify: anything
    // that prevents verification (missing/undecodable signature, no x5chain, or a
    // signer_payload span we cannot slice byte-exactly) is a signature FAILURE, not an
    // unverified pass. `false` withholds the reported summary (§8) and — under trust-on
    // — keeps the identity out of the trusted set. The ICA form's credential signature
    // is not cryptographically verified here (issuer-key resolution deferred — see
    // KNOWN-DELTAS); its summary is surfaced with signatureValid left null.
    let signatureValid: boolean | null = null
    if (sigType === CAWG_X509) {
        signatureValid = false
        if (d.signature instanceof Uint8Array) {
            const spRaw = cborMemberRaw(a.raw, 'signer_payload')
            try {
                const sign1 = decodeSign1(d.signature)
                const chain = x5chain(sign1)
                if (spRaw && chain.length > 0) {
                    const cert = parseCertificate(chain[0])
                    signatureValid = await verifyCoseSign1(sign1, spRaw, cert.spki)
                }
            } catch { signatureValid = false }
        }
    }
    return { present: true, sigType, signatureValid, codes }
}
